import ContentGuide from '@/global/guides/ContentGuide'
import { formatPorcentagemRegra } from '@acme/common'
import {
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Select,
    TextField,
} from '@mui/material'
import { Button } from 'global/components/Button'
import { FormField, FormFieldCheckbox } from 'global/components/Form'
import IconeAjuda from 'global/components/IconeAjuda'
import Dialog from 'global/guides/Dialog'
import { MinusIcon, PlusIcon } from 'lucide-react'
import { FC, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { PorcentagemRegraCalculo } from 'service/api'
import { z } from 'zod'

export default function Porcentagem() {
    const { setValue, watch } = useFormContext<{
        porcentagens: PorcentagemRegraCalculo[]
        controlarBancoHoras: boolean
    }>()

    const [controlarBancoHoras, porcentagens] = watch([
        'controlarBancoHoras',
        'porcentagens',
    ])

    function handleAdd() {
        setValue(
            'porcentagens',
            [
                ...(porcentagens || []).concat({
                    porcentagem: (() => {
                        let porcentagem = 50
                        while (
                            porcentagens?.some(
                                p => p.porcentagem === String(porcentagem),
                            )
                        )
                            porcentagem++
                        return String(porcentagem)
                    })(),
                    compensacao: 100,
                    persiste: true,
                }),
            ] as PorcentagemRegraCalculo[],
            { shouldDirty: true, shouldTouch: true },
        )
    }

    function handleRemove(index: number) {
        setValue(
            'porcentagens',
            porcentagens.filter(
                (_, i) => i !== index,
            ) as PorcentagemRegraCalculo[],
            { shouldDirty: true, shouldTouch: true },
        )
    }

    function handleUpdate(
        index: number,
        value: Partial<PorcentagemRegraCalculo>,
    ) {
        setValue(
            `porcentagens.${index}`,
            { ...porcentagens[index], ...value } as PorcentagemRegraCalculo,
            { shouldDirty: true, shouldTouch: true },
        )
    }

    return (
        <Grid container columnGap={1} rowGap={1} columns={30} mb={2}>
            {porcentagens.map((item, index) => (
                <Item
                    key={index}
                    item={item}
                    index={index}
                    handleRemove={handleRemove}
                    handleUpdate={handleUpdate}
                    controlarBancoHoras={!!controlarBancoHoras}
                />
            ))}
            <Grid item sm={controlarBancoHoras ? 14.7 : 9.7} xs={30}>
                <div className="flex justify-center w-full h-16 p-1">
                    <Button
                        onClick={handleAdd}
                        title="Adicionar porcentagem"
                        variant="outlined"
                        color="success"
                        className="size-full"
                    >
                        <PlusIcon />
                    </Button>
                </div>
            </Grid>
        </Grid>
    )
}

interface ItemProps {
    item?: PorcentagemRegraCalculo
    index: number
    handleRemove: (index: number) => void
    controlarBancoHoras: boolean
    handleUpdate: (
        index: number,
        value: Partial<PorcentagemRegraCalculo>,
    ) => void
}

const Item: FC<ItemProps> = ({
    item,
    index,
    handleRemove,
    handleUpdate,
    controlarBancoHoras,
}) => {
    if (
        !item ||
        item.porcentagem == null ||
        typeof item.compensacao !== 'number'
    ) {
        return (() => {
            handleRemove(index)
            return null
        })()
    }

    const isNumeric = !isNaN(Number(item.porcentagem))

    return (
        <Grid item sm={controlarBancoHoras ? 14.7 : 9.7} xs={30}>
            <ContentGuide.Card className="flex flex-row items-center p-2 rounded-sm justify-around h-16">
                <FormField
                    name={`porcentagens.${index}.porcentagem`}
                    type="text"
                    size="small"
                    unHelperText
                    onChange={e =>
                        handleUpdate(index, {
                            porcentagem: e.target.value,
                        })
                    }
                    InputLabelProps={{
                        shrink: true,
                    }}
                    label="Se"
                    defaultValue={item.porcentagem}
                    InputProps={{
                        endAdornment: isNumeric ? (
                            <InputAdornment position="start">%</InputAdornment>
                        ) : null,
                    }}
                    inputProps={{
                        maxLength: 5,
                    }}
                    sx={{
                        width: '6rem',
                    }}
                    autoComplete="off"
                />
                <PopupCompensa index={index} />
                {controlarBancoHoras && (
                    <div className="inline-flex items-center">
                        <FormFieldCheckbox
                            name={`porcentagens.${index}.persiste`}
                            label="Persiste"
                        />
                        <IconeAjuda className="-ml-2">
                            Se marcado, as horas extras com essa porcentagem
                            persistirão entre as competências e serão somadas ao
                            banco de horas.
                            <br />
                            Desmarque este campo apenas se você integrar essas
                            horas extras com a folha de pagamento mensalmente.
                        </IconeAjuda>
                    </div>
                )}
                <IconButton
                    color="error"
                    onClick={() => handleRemove(index)}
                    title={`Remover regra ${formatPorcentagemRegra(
                        item.porcentagem,
                    )}`}
                    className="hover:bg-error/30"
                >
                    <MinusIcon />
                </IconButton>
            </ContentGuide.Card>
        </Grid>
    )
}

interface IPropsPopupCompensa {
    index: number
}

const porcentagensPadrao = (porcentagem?: number) => {
    const PADRAO = [-1, 0, 100]
    return porcentagem ? [...PADRAO, porcentagem] : PADRAO
}

function PopupCompensa({ index }: IPropsPopupCompensa) {
    const [open, setOpen] = useState(false)
    const { watch, setValue } = useFormContext()

    const [valueWatched, porcentagemWatched] = watch([
            `porcentagens.${index}.compensacao`,
            `porcentagens.${index}.porcentagem`,
        ]),
        value = Number(valueWatched),
        porcentagemStr = String(porcentagemWatched),
        porcentagemNum = Number(porcentagemWatched),
        isNumeric = !isNaN(porcentagemNum)

    const [tmp, setTmp] = useState<string | number>(0)

    useEffect(() => {
        if (open) {
            const padrao = isNumeric
                ? porcentagensPadrao(porcentagemNum)
                : porcentagensPadrao()
            setTmp(padrao.includes(value) ? value : 'CUSTOM')
        }
    }, [open])

    return [
        <TextField
            key={`${index}-${value}-select`}
            size="small"
            variant="filled"
            InputLabelProps={{
                shrink: true,
            }}
            InputProps={{ readOnly: true }}
            sx={{
                width: '150px',
                userSelect: 'none',
            }}
            label="Compensa"
            value={value}
            onClick={() => setOpen(true)}
            select
        >
            <MenuItem value={-1}>Não compensa</MenuItem>
            <MenuItem value={0}>Hora por hora</MenuItem>
            <MenuItem value={100}>Em dobro</MenuItem>
            {isNumeric && !porcentagensPadrao().includes(value) && (
                <MenuItem value={porcentagemNum}>Percentual</MenuItem>
            )}
            {!porcentagensPadrao(
                isNumeric ? porcentagemNum : undefined,
            ).includes(value) && (
                <MenuItem value={value}>
                    {value}% ({value / 100 + 1}x)
                </MenuItem>
            )}
        </TextField>,
        <Dialog.Root
            key={`${index}-${valueWatched}`}
            keepMounted
            open={open}
            onClose={() => setOpen(false)}
            onSubmit={v => {
                setOpen(false)
                setValue(
                    `porcentagens.${index}.compensacao`,
                    Number(v.compensacao),
                )
            }}
            schema={z
                .object({
                    compensacao: z.string().or(z.number()).optional(),
                })
                .transform(v => ({
                    compensacao: Number(
                        typeof tmp === 'string' ? v.compensacao : tmp,
                    ),
                }))}
            height="auto"
            width="auto"
            defaultValues={{ compensacao: Number(value) }}
        >
            <Dialog.Content>
                <Select
                    value={tmp}
                    onChange={e => setTmp(e.target.value)}
                    variant="filled"
                    sx={{
                        width: '17rem',
                        mb: 2,
                    }}
                >
                    <MenuItem value={-1}>Não compensar</MenuItem>
                    <MenuItem value={0}>Hora por hora</MenuItem>
                    <MenuItem value={100}>Em dobro</MenuItem>
                    {isNumeric &&
                        !porcentagensPadrao().includes(porcentagemNum) && (
                            <MenuItem value={porcentagemNum}>
                                De acordo com percentual ({porcentagemStr}%)
                            </MenuItem>
                        )}
                    <MenuItem value="CUSTOM">Outro percentual</MenuItem>
                </Select>
                {tmp === 'CUSTOM' && (
                    <FormField
                        name="compensacao"
                        label="Porcentagem a compensar"
                        type="number"
                        shouldUnregister={!open}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="start">
                                    %
                                </InputAdornment>
                            ),
                        }}
                    />
                )}
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                <Dialog.Actions.PrimaryButton />
            </Dialog.Actions>
        </Dialog.Root>,
    ]
}
